import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  contentChildren,
  effect,
  inject,
  input,
  InputSignal,
  Signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { PaginatorModel } from '@libs/common/models';
import { IconComponent } from '@libs/components/icons';
import { ViewportService } from '@libs/shared/services';
import { PaginatorComponent } from '../../navigation/paginator';
import { TableColumnDirective, TableRowActionsDirective, TableRowCollapsibleDirective } from './directives';

@Component({
  selector: 'ui-table',
  templateUrl: './table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PaginatorComponent,
    MatTableModule,
    MatSortModule,
    NgTemplateOutlet,
    MatButtonModule,
    NgClass,
    IconComponent,
  ],
  animations: [
    trigger('columnDetails', [
      state('collapsed', style({ height: '0px' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TableComponent {
  private readonly viewportService: ViewportService = inject(ViewportService);

  public readonly rows: InputSignal<unknown[]> = input.required();
  public readonly responsive: InputSignal<boolean> = input(false);
  public readonly collapsible: InputSignal<boolean> = input(false);
  public readonly collapsibleRow: InputSignal<boolean> = input(false);
  public readonly collapsibleIcon: InputSignal<boolean> = input(true);
  public readonly paginatorConfig: InputSignal<PaginatorModel | undefined> = input<PaginatorModel | undefined>();

  public readonly sort: Signal<MatSort> = viewChild.required(MatSort);
  public readonly paginator: Signal<PaginatorComponent | undefined> = viewChild(PaginatorComponent);
  public readonly columns: Signal<readonly TableColumnDirective[]> = contentChildren(TableColumnDirective);
  public readonly rowCollapsibleRef: Signal<TemplateRef<unknown> | undefined> = contentChild(TableRowCollapsibleDirective, { read: TemplateRef });
  public readonly rowActionsRef: Signal<TemplateRef<unknown> | undefined> = contentChild(TableRowActionsDirective, { read: TemplateRef });

  public readonly isMobile: Signal<boolean> = toSignal(this.viewportService.isMobile(), { initialValue: false });
  public readonly isTablet: Signal<boolean> = toSignal(this.viewportService.isTablet(), { initialValue: false });

  public readonly columnsMobile: Signal<readonly TableColumnDirective[]> = computed(() => [...this.columns()].slice(1));
  public readonly columnsTablet: Signal<readonly TableColumnDirective[]> = computed(() => [...this.columns()].slice(1));

  public readonly columnsToDisplay: Signal<string[]> = computed(() => {
    const columns: readonly TableColumnDirective[] = this.columns();

    if (columns.length === 0) {
      return [];
    }

    if (this.isMobile() || this.isTablet()) {
      return [columns[0].name, this.actionsColumnName];
    }

    const displayColumns: string[] = columns.map((item: TableColumnDirective) => item.name);

    /* istanbul ignore next */
    if (this.collapsible() || this.rowActionsRef() || (this.collapsibleRow() && this.rowCollapsibleRef())) {
      return [...displayColumns, this.actionsColumnName];
    }

    return displayColumns;
  });

  public readonly dataSource: MatTableDataSource<unknown> = new MatTableDataSource<unknown>();
  public isCollapsed: boolean = false;
  public expandedRows: Set<unknown> = new Set<unknown>();
  public readonly actionsColumnName: string = 'actionsColumn';

  public constructor() {
    effect(() => {
      this.dataSource.data = this.rows();
    });

    effect(() => {
      this.dataSource.sort = this.sort();
    });

    effect(() => {
      const paginatorConfig: PaginatorModel | undefined = this.paginatorConfig();
      const paginator: PaginatorComponent | undefined = this.paginator();

      if (paginatorConfig && paginator && typeof paginator.matPaginator === 'function') {
        this.dataSource.paginator = paginator.matPaginator();
      }
    });
  }

  public toggleCollapsibleRow(element: unknown): void {
    if (this.expandedRows.has(element)) {
      this.expandedRows.delete(element);
    } else {
      this.expandedRows.add(element);
    }
  }
}
